import { Routes } from "@angular/router";
import { getAdminRole, getIncidentsCases, getOfferPackagesList, getTableSettings, getLeadsStatistics, getLeadsList, getLeadById } from "./resolvers";
import { requestTableName, RequestTableSettings, } from "./pages/requests/requests.component";
import { userGuard } from "./partials/guards/user.guard";
import {
    ROLES_CASE_PRIVILEGED,
    ROLES_EXCEPT_COMPLIANCE,
    ROLES_OPERATORS,
    ROLES_SETTINGS,
} from "./partials/guards/role-access";

export const routes: Routes = [
    {
        path: "login",
        title: "Safe My Crypto - Login",
        loadChildren: () => import("./pages/auth/auth.module"),
    },
    {
        path: "",
        loadComponent: () => import("./layout/layout.component"),
        resolve: {
            adminRole: getAdminRole
        },
        children: [
            {
                path: "logs",
                title: "Safe My Crypto - Logs",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import('./pages/logs/logs.component').then(c => c.LogsComponent)
            },
            {
                path: "settings",
                title: "Safe My Crypto - Settings",
                // ADMIN, not SUPER_ADMIN only: the admin settings endpoints and
                // the lead-pause table this page already hosts are both
                // hasRole('ADMIN') server-side.
                canActivate: [userGuard(ROLES_SETTINGS)],
                loadComponent: () => import("./pages/settings/settings.component"),
            },
            {
                path: "requests",
                title: "Safe My Crypto - Requests",
                resolve: {
                    initCaseIncidentResponse: getIncidentsCases,
                    loadedTableSettings:
                        getTableSettings<RequestTableSettings>(requestTableName),
                    adminRole: getAdminRole
                },
                loadComponent: () => import("./pages/requests/requests.component"),
            },
            {
                path: "case/:incidentId",
                title: "Safe My Crypto - Case",
                loadComponent: () => import("./pages/case/case.component").then(c => c.CaseComponent),
                children: [
                    {
                        path: "tabs",
                        children: [
                            {
                                path: "tab-case",
                                loadComponent: () => import("./pages/case/tabs/tab-case/tab-case.component").then(c => c.TabCaseComponent)
                            },
                            {
                                path: "tab-offer",
                                loadComponent: () => import("./pages/case/tabs/tab-offer/tab-offer.component").then(c => c.TabOfferComponent)
                            },
                            {
                                path: "tab-invoices",
                                canActivate: [userGuard(ROLES_CASE_PRIVILEGED)],
                                loadComponent: () => import("./pages/case/tabs/tab-invoices/tab-invoices.component").then(c => c.TabInvoicesComponent)
                            },
                            {
                                path: "tab-reports",
                                loadComponent: () => import("./pages/case/tabs/tab-reports/tab-reports.component").then(c => c.TabReportsComponent)
                            },
                            {
                                path: "tab-logs",
                                canActivate: [userGuard(ROLES_CASE_PRIVILEGED)],
                                loadComponent: () => import("./pages/case/tabs/tab-logs/tab-logs.component").then(c => c.TabLogsComponent)
                            },
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'tab-case'
                            }
                        ]
                    },
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'tabs/tab-case'
                    }
                ],
            },
            {
                path: "case-star-compliance/:incidentId",
                title: "Safe My Crypto - STAR Compliance Case",
                loadComponent: () => import("./pages/case-star-compliance/case-star-compliance.component").then(c => c.CaseStarComplianceComponent),
            },
            {
                path: "tether-emissions",
                title: "Safe My Crypto - Tether Emissions",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import("./pages/tether-emissions/tether-emissions.component"),
            },
            {
                path: "tether-emission/:emissionId",
                title: "Safe My Crypto - Tether Emission Detail",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import("./pages/tether-emission-detail/tether-emission-detail.component"),
            },
            {
                path: "kyc",
                title: "Safe My Crypto - KYC",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import("./pages/kyc/kyc.component"),
            },
            {
                path: "documents",
                title: "Safe My Crypto - Documents",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import("./pages/documents/documents.component"),
            },
            {
                path: "invoices",
                title: "Safe My Crypto - Invoices",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import("./pages/invoices/invoices.component"),
            },
            {
                path: "packages",
                title: "Safe My Crypto - Packages",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                resolve: {
                    initialActiveOfferPackages: getOfferPackagesList({
                        isEssential: true,
                        isDeleted: false,
                    }),
                    initialDeletedOfferPackages: getOfferPackagesList({
                        isEssential: true,
                        isDeleted: true,
                    }),
                },
                loadComponent: () => import("./pages/packages/packages.component"),
            },
            {
                path: "operators",
                title: "Safe My Crypto - Operators",
                canActivate: [userGuard(ROLES_OPERATORS)],
                loadComponent: () => import("./pages/operators/operators.component"),
            },
            {
                path: "leads",
                title: "Safe My Crypto - Leads",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import("./pages/leads/leads.component"),
                resolve: {
                    leadsStatistics: getLeadsStatistics,
                    leads: getLeadsList,
                },
            },
            {
                path: "lead/:leadId",
                title: "Safe My Crypto - Lead Detail",
                canActivate: [userGuard(ROLES_EXCEPT_COMPLIANCE)],
                loadComponent: () => import("./pages/lead-detail/lead-detail.component"),
                resolve: {
                    lead: getLeadById,
                },
            },
            {
                path: "404",
                title: "Safe My Crypto - 404",
                loadComponent: () =>
                    import("./pages/not-found/not-found.component"),
            },
            {
                path: "something-went-wrong",
                title: "Safe My Crypto - Upsss",
                loadComponent: () =>
                    import(
                        "./pages/something-went-wrong/something-went-wrong.component"
                        ),
            },
            {
                path: "",
                title: "Safe My Crypto",
                pathMatch: "full",
                loadComponent: () =>
                    import("./pages/welcome/welcome.component"),
            },
        ],
    },
    {
        path: "**",
        redirectTo: "404",
    },
];
